import { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Bell, X, Check } from 'lucide-react';

const isProduction = typeof window !== 'undefined' && (
  window.location.hostname === 'rekrutor.com' || 
  window.location.hostname === 'www.rekrutor.com' ||
  window.location.hostname.endsWith('.emergent.host')
);
const API = isProduction ? '/api' : `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export const PushNotificationPrompt = ({ onClose, onEnabled }) => {
  const [enabling, setEnabling] = useState(false);
  const [error, setError] = useState(null);

  const urlBase64ToUint8Array = (base64String) => {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const enablePushNotifications = async () => {
    setEnabling(true);
    setError(null);

    try {
      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        const registration = await navigator.serviceWorker.ready;
        
        const keyResponse = await fetch(`${API}/notifications/vapid-public-key`, {
          credentials: "include",
        });
        const keyData = await keyResponse.json();
        
        if (keyData.key) {
          const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(keyData.key),
          });
          
          await fetch(`${API}/notifications/subscribe`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ subscription: subscription.toJSON() }),
          });
          
          localStorage.setItem('push-notifications-enabled', 'true');
          onEnabled?.();
        } else {
          localStorage.setItem('push-notifications-enabled', 'true');
          onEnabled?.();
        }
      } else if (permission === 'denied') {
        setError('Notifications were blocked. You can enable them in browser settings.');
      }
    } catch (err) {
      console.error('Push subscription failed:', err);
      setError('Failed to enable notifications. Please try again.');
    } finally {
      setEnabling(false);
    }
  };

  const handleSkip = () => {
    localStorage.setItem('push-prompt-skipped', new Date().toISOString());
    onClose?.();
  };

  return (
    <div 
      data-testid="push-notification-prompt"
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4"
    >
      <div className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl p-6 max-w-sm w-full animate-in zoom-in-95">
        <button 
          onClick={handleSkip}
          className="absolute top-4 right-4 text-slate-400 hover:text-white"
          data-testid="push-prompt-close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-crimson to-red-700 flex items-center justify-center">
            <Bell className="w-8 h-8 text-white" />
          </div>
          
          <h3 className="text-xl font-bold text-white mb-2">Stay Updated</h3>
          <p className="text-slate-400 text-sm mb-6">
            Get instant notifications for new messages, job matches, and interview invitations
          </p>

          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          <div className="space-y-3">
            <Button
              onClick={enablePushNotifications}
              disabled={enabling}
              className="w-full bg-crimson hover:bg-crimson/90 text-white"
              data-testid="enable-push-notifications-btn"
            >
              {enabling ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <Bell className="w-4 h-4 mr-2" />
              )}
              Enable Notifications
            </Button>
            
            <Button
              onClick={handleSkip}
              variant="ghost"
              className="w-full text-slate-400 hover:text-white"
              data-testid="skip-push-notifications-btn"
            >
              Maybe Later
            </Button>
          </div>

          <p className="text-slate-500 text-xs mt-4">
            You can change this anytime in Settings
          </p>
        </div>
      </div>
    </div>
  );
};

export default PushNotificationPrompt;
